import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useProject } from '@contexts/Project.context';
import { getLatestJob } from '@utils/api';
import { invalidateCollectionResults } from '@utils/collectionQueries';
import { JOB_STATUS, JOB_TYPE, TERMINAL_JOB_STATUSES } from '@utils/constants';
import { getJobCollectionUuid } from '@utils/detectionJob';
import { useObjectPickerStore } from '@stores/useObjectPickerStore';

import { useShowCollectionResults } from '@hooks/useShowCollectionResults';

const terminalStatuses = new Set(TERMINAL_JOB_STATUSES);
const POLL_INTERVAL_MS = 5000;

/**
 * No selection was recorded for the job being handled — distinct from "nothing
 * was selected", which is a recorded `null`.
 */
const NOT_RECORDED = Symbol('no recorded selection');

/**
 * Tracks the project's detection job and reacts to its outcome.
 *
 * Polling is the only completion signal the backend offers, so "the job
 * finished" means observing the running → terminal transition on this poll.
 *
 * Two responsibilities:
 * - **Resume**: on project entry, reopen the progress card if the latest job is
 *   a detection still in progress. This covers the page-reload case where the
 *   in-memory `activeJobUuid` was lost — in-app navigation already keeps it via
 *   the store.
 * - **Complete**: when a detection run we were watching succeeds, put its
 *   results on the map — select the collection that ran and refresh the cached
 *   results — so the user never has to reload or toggle a layer.
 *
 * No-ops when the project has no jobs, or when the latest job is a different
 * type.
 *
 * @param {string | undefined} projectUuid
 * @returns {{ data: object | undefined, isError: boolean, jobCollectionUuid: string | null }}
 */
export const useResumeDetectionJob = projectUuid => {
	const activeJobUuid = useObjectPickerStore(s => s.activeJobUuid);
	const setActiveJobProgressModal = useObjectPickerStore(s => s.setActiveJobProgressModal);
	const queryClient = useQueryClient();
	const { collectionUuid } = useProject();
	const showCollectionResults = useShowCollectionResults();

	// `showCollectionResults` closes over react-router's setter, so its identity
	// changes every render. Reading it through a ref keeps it out of the effect's
	// dependencies — otherwise the completion handler would re-run on every
	// unrelated render. Assigned in an effect rather than during render, so a
	// render React discards can't publish its closure.
	const showCollectionResultsRef = useRef(showCollectionResults);
	useEffect(() => {
		showCollectionResultsRef.current = showCollectionResults;
	}, [showCollectionResults]);

	// Detection jobs this hook has seen running. A job we watched run is "ours",
	// so its completion is what should pull results onto the map. Adopting it from
	// the poll rather than trusting `activeJobUuid` alone also covers a reload
	// mid-run, which drops the in-memory uuid while the job keeps going.
	const watchedJobRef = useRef(null);
	// What the map was showing when the watched job was adopted, so a run that
	// finishes minutes later can tell "still where they left it" from "the user
	// has since opened something else".
	const selectionAtAdoptionRef = useRef(NOT_RECORDED);
	// Terminal job whose outcome has already been handled, so a re-render or a
	// late poll of the same job can't reselect the collection or invalidate twice.
	const handledJobRef = useRef(null);

	const { data, isError } = useQuery({
		queryKey: ['latestJob', projectUuid, activeJobUuid],
		queryFn: () => getLatestJob(projectUuid),
		enabled: Boolean(projectUuid),
		refetchInterval: query => {
			const status = query.state.data?.job_status;
			return status && terminalStatuses.has(status) ? false : POLL_INTERVAL_MS;
		},
	});

	const jobUuid = data?.job_uuid;
	const jobStatus = data?.job_status;
	const isDetection = data?.job_type === JOB_TYPE.OBJECT_DETECTION_PREDICT;
	const jobCollectionUuid = isDetection ? getJobCollectionUuid(data) : null;

	useEffect(() => {
		if (!isDetection || !jobUuid) return;

		if (!terminalStatuses.has(jobStatus)) {
			// Only on adoption: re-recording on every poll would track the user's
			// selection instead of the one the run started against.
			if (watchedJobRef.current !== jobUuid) {
				watchedJobRef.current = jobUuid;
				selectionAtAdoptionRef.current = collectionUuid ?? null;
			}
			setActiveJobProgressModal(true);
			return;
		}

		if (handledJobRef.current === jobUuid) return;
		handledJobRef.current = jobUuid;

		const watched = watchedJobRef.current === jobUuid;
		// A job claimed through `activeJobUuid` alone was started in this session
		// and finished before we ever saw it run — there was no window for the user
		// to move on, so there's no recorded selection to compare against.
		const adoptedSelection = watched ? selectionAtAdoptionRef.current : NOT_RECORDED;
		const wasWatched = watched || activeJobUuid === jobUuid;
		watchedJobRef.current = null;
		selectionAtAdoptionRef.current = NOT_RECORDED;

		// Failed/killed/cancelled produce no new results. The progress modal
		// reports the outcome; there's nothing to render.
		if (jobStatus !== JOB_STATUS.FINISHED) return;

		// The user opened a different collection while the run was going, so the
		// map is deliberately showing something else. Clearing the selection isn't
		// "moving on" — the results are still what they asked for.
		const movedOn =
			adoptedSelection !== NOT_RECORDED &&
			Boolean(collectionUuid) &&
			collectionUuid !== adoptedSelection;

		// The run we were watching succeeded. Show it: the collection that ran
		// isn't necessarily the one on screen, and a first-ever run means the map
		// currently has nothing for it at all.
		if (wasWatched && jobCollectionUuid && !movedOn) {
			showCollectionResultsRef.current(jobCollectionUuid);
			return;
		}

		// Either this job was already finished before we started watching (entering
		// a project whose latest job happens to be a completed detection), the user
		// has since opened another collection, or the payload didn't say which
		// collection ran. Refresh what's cached so results and the detected-area
		// boundary catch up, without taking the map away from what the user chose.
		invalidateCollectionResults(queryClient);
	}, [
		isDetection,
		jobUuid,
		jobStatus,
		jobCollectionUuid,
		activeJobUuid,
		collectionUuid,
		setActiveJobProgressModal,
		queryClient,
	]);

	return { data, isError, jobCollectionUuid };
};
